/**
 * PORTED — RO-3-00c / #44470.
 * Origin: `excellent/app/_modes/mode-section-route.tsx`.
 *
 * Changes:
 *   `resolveTenant` / `getLaneRow` / `sectionTouch` → `useApi(modes.resolveSection)`
 *     for existence; pure domain resolution stays in `@/modes/domains`
 *   `redirect` / `notFound` from next → `useNavigate` + null render
 *   `sectionTouch` metric redirects preserved client-side via navigate
 *
 * Renders the same `ModePage` the `?area=` form does once the section resolves.
 */

import { useEffect, useMemo } from "react";
import { useNavigate } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import { ModePage } from "./mode-page";
import {
  domainForPathSegment,
  getDomain,
  getDomainView,
  areaToPath,
} from "@/modes/domains";
import { type ModeSlug } from "@/modes/modes-registry";

/**
 * Dynamic Pulse P6 insight hand-map — unambiguous touchpoint → seriesId.
 * Kept identical to the Next original.
 */
const INSIGHT_FOR_TOUCH: Record<string, string> = {
  "work:throughput": "work.completed",
  "work:scheduled": "work.duecov",
};

export function ModeSectionRoute({
  mode,
  segments,
  searchParams,
  projectSlug,
}: {
  mode: ModeSlug;
  segments: string[];
  searchParams: Record<string, string | string[] | undefined>;
  projectSlug?: string;
}) {
  const navigate = useNavigate();
  const [sectionSeg, viewSeg, metricSeg] = segments;
  const base = projectSlug ? `/projects/${projectSlug}/${mode}` : `/${mode}`;

  useEffect(() => {
    if (!sectionSeg) navigate.replace(base);
  }, [sectionSeg, base, navigate]);

  const domain = sectionSeg ? domainForPathSegment(mode, sectionSeg) : null;
  const aliased = sectionSeg ? getDomain(mode, sectionSeg) : null;
  const hiddenAlias = Boolean(aliased?.hidden);

  // When neither a visible domain nor a hidden alias, ask the host if it's a lane.
  const needsLaneCheck = Boolean(sectionSeg && !domain && !aliased);
  const { data: resolved, isLoading } = useApi(
    modes.resolveSection,
    needsLaneCheck && sectionSeg
      ? { query: { mode, section: sectionSeg } }
      : null,
  );

  const area = useMemo(() => {
    if (!sectionSeg) return undefined;
    if (domain) return domain.slug;
    return sectionSeg;
  }, [sectionSeg, domain]);

  const found = domain || aliased || (resolved?.found ?? false);

  // Metric fold redirects (client-side). Seam navigate is {push,replace,…}.
  useEffect(() => {
    if (!sectionSeg || !area) return;
    const view = domain && viewSeg ? getDomainView(domain, viewSeg) : null;
    // VIEW metric
    if (domain && view && metricSeg) {
      const insight = INSIGHT_FOR_TOUCH[`${mode}:${metricSeg}`];
      const parent = `${base}/${sectionSeg}/${viewSeg}`;
      navigate.replace(
        insight ? `${parent}?insight=${encodeURIComponent(insight)}` : parent,
      );
      return;
    }
    // SECTION / LANE metric (heuristic: second segment, not a view)
    if (viewSeg && !view && !metricSeg) {
      // We cannot call sectionTouch (core/server) here; only the two known
      // INSIGHT_FOR_TOUCH keys redirect with insight, others stay on the section.
      const insight = INSIGHT_FOR_TOUCH[`${mode}:${viewSeg}`];
      if (insight) {
        navigate.replace(
          `${base}/${sectionSeg}?insight=${encodeURIComponent(insight)}`,
        );
      }
    }
  }, [sectionSeg, viewSeg, metricSeg, domain, area, base, mode, navigate]);

  if (!sectionSeg) return null;
  if (needsLaneCheck && isLoading) return null;
  if (!found) {
    return (
      <div className="px-6 py-8 type-body text-muted-foreground">
        Section not found.
      </div>
    );
  }

  const view = domain && viewSeg ? getDomainView(domain, viewSeg) : null;
  const sub =
    view ? viewSeg : hiddenAlias && viewSeg && !metricSeg ? viewSeg : undefined;

  return (
    <ModePage
      mode={mode}
      view="dashboard"
      area={area}
      sub={sub}
      searchParams={searchParams}
      projectSlug={projectSlug}
    />
  );
}

/**
 * Legacy `?area=` → canonical section PATH redirect helper (client).
 * Call from a mode route before rendering ModePage.
 */
export function redirectAreaToPath(
  mode: ModeSlug,
  sp: Record<string, string | string[] | undefined>,
  navigate: { replace: (href: string) => void },
): boolean {
  const area = typeof sp.area === "string" ? sp.area : undefined;
  if (!area) return false;
  const d = getDomain(mode, area);
  if (d?.hidden) return false;
  const sub = typeof sp.sub === "string" ? sp.sub : undefined;
  const base = areaToPath(mode, area, sub);
  const extra = new URLSearchParams();
  for (const [k, v] of Object.entries(sp)) {
    if (k === "area" || k === "sub" || k === "view") continue;
    if (typeof v === "string") extra.set(k, v);
  }
  const qs = extra.toString();
  navigate.replace(qs ? `${base}?${qs}` : base);
  return true;
}
